
import React, { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  Settings, 
  Layers, 
  Server, 
  Package, 
  Home
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { EquipamentoSolar } from "@/types";
import { handleFileUpload } from "@/components/solar/configuracao/utils/fileUploadHandlers";
import { EquipamentosTab } from "@/components/solar/configuracao/tabs/EquipamentosTab";
import { SistemasTab } from "@/components/solar/configuracao/tabs/SistemasTab";
import { EstruturasTab } from "@/components/solar/configuracao/tabs/EstruturasTab";
import { InstalacaoTab } from "@/components/solar/configuracao/tabs/InstalacaoTab";

const KitSolarConfigPage = () => {
  const [activeTab, setActiveTab] = useState("equipamentos");
  const [configAutoTab, setConfigAutoTab] = useState("tradicional");
  const navigate = useNavigate();
  
  // Form para estruturas
  const estruturasForm = useForm({
    defaultValues: {
      estruturas: [
        { tipo: "metalica", valorPorKwp: 250, descricao: "Estrutura metálica para telhado" },
        { tipo: "ceramica", valorPorKwp: 280, descricao: "Estrutura para telhado cerâmico" },
        { tipo: "fibrocimento", valorPorKwp: 270, descricao: "Estrutura para telhado de fibrocimento" },
        { tipo: "solo", valorPorKwp: 400, descricao: "Estrutura para solo" },
        { tipo: "laje", valorPorKwp: 320, descricao: "Estrutura para laje" }
      ]
    }
  });
  
  // Using useFieldArray for estruturas
  const { fields: estruturaFields } = useFieldArray({
    control: estruturasForm.control,
    name: "estruturas"
  });
  
  // Estados para categorias
  const [categoriasOutros, setCategoriasOutros] = useState<{ categoria: string; itens: string[] }[]>([]);
  
  // Estados para upload de imagens
  const [painelImagePreviewTradicional, setPainelImagePreviewTradicional] = useState("");
  const [inversorImagePreviewTradicional, setInversorImagePreviewTradicional] = useState("");
  const [painelImagePreviewMLPE, setPainelImagePreviewMLPE] = useState("");
  const [inversorImagePreviewMLPE, setInversorImagePreviewMLPE] = useState("");
  const [painelImagePreviewHibrido, setPainelImagePreviewHibrido] = useState("");
  const [inversorImagePreviewHibrido, setInversorImagePreviewHibrido] = useState("");
  const [painelImagePreviewOffGrid, setPainelImagePreviewOffGrid] = useState("");
  const [inversorImagePreviewOffGrid, setInversorImagePreviewOffGrid] = useState("");
  
  // Estados para arquivos de imagem
  const [painelImageTradicional, setPainelImageTradicional] = useState<File | null>(null);
  const [inversorImageTradicional, setInversorImageTradicional] = useState<File | null>(null);
  const [painelImageMLPE, setPainelImageMLPE] = useState<File | null>(null);
  const [inversorImageMLPE, setInversorImageMLPE] = useState<File | null>(null);
  const [painelImageHibrido, setPainelImageHibrido] = useState<File | null>(null);
  const [inversorImageHibrido, setInversorImageHibrido] = useState<File | null>(null);
  const [painelImageOffGrid, setPainelImageOffGrid] = useState<File | null>(null);
  const [inversorImageOffGrid, setInversorImageOffGrid] = useState<File | null>(null);
  
  // Carregar dados de categorias
  useEffect(() => {
    // Carregar categorias de outros itens
    const categoriasOutrosStorage = localStorage.getItem('categoriasOutros');
    
    if (categoriasOutrosStorage) {
      try {
        setCategoriasOutros(JSON.parse(categoriasOutrosStorage));
      } catch (error) {
        console.error("Erro ao carregar categorias de outros itens:", error);
      }
    }
  }, []);
  
  // Manipulador para excluir item de categoria
  const handleExcluirItemCategoria = (categoria: string, item: string) => {
    const categoriaAtualizada = categoriasOutros.find(cat => cat.categoria === categoria);
    
    if (categoriaAtualizada) {
      const itensAtualizados = categoriaAtualizada.itens.filter(i => i !== item);
      
      if (itensAtualizados.length === 0) {
        // Se não sobrar nenhum item, remove a categoria inteira
        setCategoriasOutros(categoriasOutros.filter(cat => cat.categoria !== categoria));
      } else {
        // Atualiza apenas os itens da categoria
        setCategoriasOutros(categoriasOutros.map(cat => 
          cat.categoria === categoria 
            ? { ...cat, itens: itensAtualizados } 
            : cat
        ));
      }
      
      toast.success("Item removido com sucesso!");
    }
  };
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-green-700 flex items-center gap-2">
              <Settings className="h-8 w-8" /> Configuração do Sistema Solar
            </h1>
            <p className="text-gray-600 mt-1">
              Gerencie equipamentos, preços, estruturas e configurações do sistema fotovoltaico
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate("/solar")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar para Solar
          </Button>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full h-auto p-1">
            <TabsTrigger 
              value="equipamentos" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
            >
              <Package className="h-4 w-4" />
              <span>Equipamentos</span>
            </TabsTrigger>
            <TabsTrigger 
              value="sistemas" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
            >
              <Server className="h-4 w-4" />
              <span>Sistemas</span>
            </TabsTrigger>
            <TabsTrigger 
              value="estruturas" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
            >
              <Layers className="h-4 w-4" />
              <span>Estruturas</span>
            </TabsTrigger>
            <TabsTrigger 
              value="instalacao" 
              className="flex items-center gap-2 py-3 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
            >
              <Home className="h-4 w-4" />
              <span>Instalação</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="equipamentos">
            <EquipamentosTab
              configAutoTab={configAutoTab}
              setConfigAutoTab={setConfigAutoTab}
            />
          </TabsContent>
          
          <TabsContent value="sistemas">
            <SistemasTab
              painelImagePreviewTradicional={painelImagePreviewTradicional}
              inversorImagePreviewTradicional={inversorImagePreviewTradicional}
              setPainelImagePreviewTradicional={setPainelImagePreviewTradicional}
              setInversorImagePreviewTradicional={setInversorImagePreviewTradicional}
              setPainelImageTradicional={setPainelImageTradicional}
              setInversorImageTradicional={setInversorImageTradicional}
              handleFileUpload={handleFileUpload}
            />
          </TabsContent>
          
          <TabsContent value="estruturas">
            <EstruturasTab
              estruturaFields={estruturaFields}
              estruturasForm={estruturasForm}
              categoriasOutros={categoriasOutros}
              handleExcluirItemCategoria={handleExcluirItemCategoria}
            />
          </TabsContent>
          
          <TabsContent value="instalacao">
            <InstalacaoTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default KitSolarConfigPage;
